import type { TagRequest, TagRequestItemStatus, TagRequestPerson, TagRequestStatus } from '@/client/types/tag-requests';

export const REQUEST_STATUS_BADGE: Record<TagRequestStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-amber-100 text-amber-800',
  under_review: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  partially_approved: 'bg-orange-100 text-orange-800',
  rejected: 'bg-red-100 text-red-800',
  withdrawn: 'bg-gray-200 text-gray-700',
};

export const REQUEST_STATUS_LABEL: Record<TagRequestStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under review',
  approved: 'Approved',
  partially_approved: 'Partly approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

export const ITEM_STATUS_BADGE: Record<TagRequestItemStatus, string> = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  withdrawn: 'bg-gray-200 text-gray-700',
};

/** Shown to the lister as the per-tag reason; keep codes stable (ff-api stores them verbatim). */
export const REJECT_REASONS: { code: string; label: string }[] = [
  { code: 'evidence_insufficient', label: 'Evidence insufficient' },
  { code: 'evidence_expired', label: 'Evidence expired' },
  { code: 'not_eligible', label: 'Listing not eligible' },
  { code: 'evidence_mismatch', label: "Evidence doesn't match the listing" },
  { code: 'duplicate', label: 'Duplicate request' },
  { code: 'other', label: 'Other (explain in notes)' },
];

/** Decisions are accepted while any tag is pending; ff-api refuses only withdrawn requests. */
export const isDecidable = (status: TagRequestStatus) =>
  status === 'submitted' || status === 'under_review' || status === 'draft';

export const titleCaseTag = (name?: string | null) =>
  name ? name.replace(/\b\p{L}/gu, (c) => c.toUpperCase()) : 'Tag';

export const humanizeCode = (code?: string | null) =>
  code ? code.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '';

export const personLabel = (p?: TagRequestPerson | null) =>
  p ? p.name || p.username || p.email || `User #${p.uid}` : '—';

export const listingLabel = (req: TagRequest) => req.entity?.name || `Listing #${req.eid}`;

/** Org requests are filed for the org; name it first, then the member who submitted. */
export const requesterLabel = (req: TagRequest) =>
  req.oid ? req.organization?.name || `Organization #${req.oid}` : personLabel(req.requester ?? (req.uid ? { uid: req.uid } : null));
